import React, { useEffect, useState } from "react";

import { District, RowData } from "@hubcapp/components/DistrictsTable/types";
import { useQueryRegion } from "@hubcapp/hooks";
import { useTealium } from "@hubcapp/context";

import { DistrictCoverageStateProviderProps, ViewMode } from "./types";

import { District as AvailableDistrict, Region } from "@hubcapp/types";
import {
  mapDistrictsToTableData,
  transformToStateData,
  transformToUpdatedDistricts,
} from "./helpers";
import DistrictCoverage from "./DistrictCoverage";

const DistrictCoverageStateProvider: React.FC<DistrictCoverageStateProviderProps> = ({
  areaId,
  areaName,
  areaShortCode,
  instructorDistricts,
  onDistrictsUpdate = () => {},
  loading = false,
  canEdit,
  title = "Set my district coverage",
}) => {
  const [viewMode, setViewMode] = useState(ViewMode.View);
  const [rowData, setRowData] = useState<RowData[]>();
  const [allDistricts, setAllDistricts] = useState<District[]>();
  const [currentStateDistricts, setCurrentStateDistricts] = useState<
    District[]
  >();
  const { trackEvent } = useTealium();

  useEffect(() => {
    setRowData(mapDistrictsToTableData(instructorDistricts, allDistricts));
  }, [instructorDistricts, allDistricts]);

  const onCompleted = (data: { region: Region }) => {
    const districts = data?.region?.districts;
    if (!districts) return;
    setAllDistricts(transformToStateData(districts as AvailableDistrict[]));
  };

  const { loading: areaQueryLoading } = useQueryRegion(areaId, onCompleted);

  const toggleEdit = () => {
    trackEvent({
      eventCategory: "Areas & prices - Edit My District",
      eventAction: "Button Click",
      eventLabel: "Edit Districts",
    });
    setViewMode(viewMode === ViewMode.View ? ViewMode.Edit : ViewMode.View);
  };

  const confirmChanges = () => {
    if (!rowData) return;
    if (!currentStateDistricts) {
      const updatedDistricts = transformToUpdatedDistricts(rowData);
      setCurrentStateDistricts(updatedDistricts);
      completeConfirm(updatedDistricts);
    } else {
      completeConfirm(currentStateDistricts);
    }
  };

  const completeConfirm = (districts: District[]) => {
    onDistrictsUpdate(districts);
    setViewMode(viewMode === ViewMode.View ? ViewMode.Edit : ViewMode.View);
    trackEvent({
      eventCategory: "Areas & prices - Edit My District",
      eventAction: "Button Click",
      eventLabel: "Confirm Changes",
    });
  };

  const handleDistrictsUpdate = (districts: RowData[]) => {
    setRowData(districts);
    const updatedDistricts = transformToUpdatedDistricts(districts);
    setCurrentStateDistricts(updatedDistricts);
    onDistrictsUpdate(updatedDistricts || []);
  };

  return (
    <DistrictCoverage
      loading={loading || areaQueryLoading}
      canEdit={canEdit}
      districts={rowData}
      mode={viewMode}
      title={title}
      areaName={areaName}
      areaShortCode={areaShortCode}
      toggleEdit={toggleEdit}
      totalDistricts={allDistricts?.length || 0}
      onDistrictsUpdate={handleDistrictsUpdate}
      onConfirm={confirmChanges}
    />
  );
};

export default DistrictCoverageStateProvider;
